<script lang="ts">
import { defineComponent, inject, computed, ref, PropType, defineEmits, getCurrentInstance } from "vue";
import { Page } from "@/utils/enum";
import { useGoogleAnalytics, Event } from "@/composables/useGoogleAnalytics";
import CreateEditContact from "@/components/Contact/CreateEditContact.vue";
import {
  ContactCurrentUserPermissions,
  ContactGroup,
} from "@/models/dto/ContactGroup";
import { getDisabledUserRoles } from "./utils/helpers";
import { URMRoles } from "@/models/dto/userRoleManagement";
import { ContactType } from "./utils/helpers";

export default defineComponent({
  name: "AddContactToReservation",
  components: {
    CreateEditContact: CreateEditContact,
  },
  props: {
    reservationId: { type: Number, required: true },
    contactGroups: { type: Array as PropType<ContactGroup[]>, required: true },
    userCreatePermissions: {
      type: Object as PropType<ContactCurrentUserPermissions>,
      required: true,
    },
  },
  setup(props, { emit }) {
    const { proxy } = getCurrentInstance()!
    const t = proxy.$t.bind(proxy)
    const showDialog = ref(false)
    const analytics = useGoogleAnalytics()
    const disabledUserRoles = computed(() => {
      return getDisabledUserRoles(props.userCreatePermissions)
    });

    // TODO: Update these once we know what we want to track
    const isInReservationDetail = inject("isInReservationDetail", false);
    const isInProvider = inject("isInProvider", false);
    const isInCheckout = inject("isInCheckout", false);
    const isInGuestCheckout = inject("isInGuestCheckout", false);



    const page = computed(() => {
      if (isInReservationDetail) {
        return Page.ReservationDetail;
      }
      if (isInProvider) {
        return Page.Provider;
      }
      if (isInCheckout || isInGuestCheckout) {
        return Page.Checkout;
      }
      return Page.Other;
    });

    const trackShareReservation = () => {
      analytics.trackEvent(Event.ShareReservation, {
        page: page.value,
      });
    };

    const handleAddContact = async (
    ) => {
        trackShareReservation();
        showDialog.value = false;
        emit('refresh');
    };

    const handleDialogInput = (val: boolean) => {
      showDialog.value = val;
    };

    const defaultUserType = computed(() => {
      if (props.userCreatePermissions.canModifyAuthorizedUserRole) {
        return URMRoles.TripAuthorizedUser.key
      }
      return URMRoles.TripViewOnly.key
    })

    const contactType = ContactType.Reservation;

    return {
      t,
      showDialog,
      handleDialogInput,
      defaultUserType,
      handleAddContact,
      disabledUserRoles,
      contactType
    };
  },
});
</script>
<template>
  <CreateEditContact
    :value="showDialog"
    @input="handleDialogInput"
    :title="$t('common.SHARE_RESERVATION').toString()"
    subtitle="You can invite others to help manage this booking!"
    :submit-button-text="$t('common.SEND_INVITE').toString()"
    :contact-groups="contactGroups ?? []"
    :header-action-text="$t('common.CLOSE').toString()"
    :default-user-type="defaultUserType"
    :default-disabled-user-roles="disabledUserRoles"
    :is-edit-mode="false"
    @add:contact="handleAddContact"
    :contact-type="contactType"
    :entity-id="reservationId"
  >
    <template #activator="{ on, attrs }">
        <span class="text-blue font-bold font-14 cursor-pointer" v-on="on" v-bind="attrs">Add Users</span>
    </template>
  </CreateEditContact>
</template>
